import * as Vue from 'vue';
import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
import * as PrimeVueConfig from 'primevue/config';
import * as PrimeVueCore from '@primevue/core';
import * as PrimeUixStyled from '@primeuix/styled';
import * as PrimeUixUtils from '@primeuix/utils';
import Aura from '@primevue/themes/aura';
import { definePreset } from '@primevue/themes';
import 'primeicons/primeicons.css';

import App from './App.vue';
import { router } from './router';
import './style.css';

// Setup federation shared scope BEFORE loading remote components
// This ensures remote bundles use the same Vue and PrimeVue modules as the host
function setupFederationSharedScope() {
  const g = globalThis as unknown as { __federation_shared__?: Record<string, Record<string, Record<string, unknown>>> };
  g.__federation_shared__ = g.__federation_shared__ || {};
  const shared = g.__federation_shared__;
  shared.default = shared.default || {};

  const vueVersion = Vue.version || '3.5.0';
  const primeVersion = '4.5.0';

  // Share Vue - critical for all reactive features
  shared.default.vue = shared.default.vue || {};
  shared.default.vue[vueVersion] = {
    get: () => Promise.resolve(() => Vue),
    loaded: true,
    from: 'host'
  };

  // Share @primevue/core - contains PrimeVueSymbol used for provide/inject
  shared.default['@primevue/core'] = shared.default['@primevue/core'] || {};
  shared.default['@primevue/core'][primeVersion] = {
    get: () => Promise.resolve(() => PrimeVueCore),
    loaded: true,
    from: 'host'
  };

  // Share @primeuix/styled - CSS-in-JS styling engine
  shared.default['@primeuix/styled'] = shared.default['@primeuix/styled'] || {};
  shared.default['@primeuix/styled'][primeVersion] = {
    get: () => Promise.resolve(() => PrimeUixStyled),
    loaded: true,
    from: 'host'
  };

  // Share @primeuix/utils - shared utilities (matchRegex, etc.)
  shared.default['@primeuix/utils'] = shared.default['@primeuix/utils'] || {};
  shared.default['@primeuix/utils'][primeVersion] = {
    get: () => Promise.resolve(() => PrimeUixUtils),
    loaded: true,
    from: 'host'
  };

  // Share primevue/config - for usePrimeVue composable
  shared.default['primevue/config'] = shared.default['primevue/config'] || {};
  shared.default['primevue/config'][primeVersion] = {
    get: () => Promise.resolve(() => PrimeVueConfig),
    loaded: true,
    from: 'host'
  };

  // Share primevue main package
  shared.default.primevue = shared.default.primevue || {};
  shared.default.primevue[primeVersion] = {
    get: () => Promise.resolve(() => ({ default: PrimeVue })),
    loaded: true,
    from: 'host'
  };

  console.log('[Host] Federation shared scope initialized');
}

// Initialize shared scope before creating app
setupFederationSharedScope();

interface ThemeConfig {
  mode: 'light' | 'dark';
  primary: string;
  primaryLight: string;
  palette: string;
}

// Initialize theme early from localStorage before components mount
function initializeTheme(): ThemeConfig {
  const THEME_STORAGE_KEY = 'proteus-theme';
  const defaultTheme: ThemeConfig = {
    mode: 'light',
    primary: '#3b82f6',
    primaryLight: '#60a5fa',
    palette: 'blue'
  };

  let theme = defaultTheme;
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      theme = { ...defaultTheme, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load theme from localStorage', e);
  }

  // Apply class to html element (for both PrimeVue and playground CSS)
  const html = document.documentElement;
  if (theme.mode === 'dark') {
    html.classList.add('dark-mode');
    html.classList.remove('light-mode');
  } else {
    html.classList.add('light-mode');
    html.classList.remove('dark-mode');
  }

  // Set CSS custom properties on root
  html.style.setProperty('--pf-primary', theme.primary);
  html.style.setProperty('--pf-primary-light', theme.primaryLight);

  return theme;
}

// Initialize theme before anything else
const savedTheme = initializeTheme();

// Create custom preset with saved primary color and togglebutton styling
const CustomAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: `{${savedTheme.palette}.50}`,
      100: `{${savedTheme.palette}.100}`,
      200: `{${savedTheme.palette}.200}`,
      300: `{${savedTheme.palette}.300}`,
      400: `{${savedTheme.palette}.400}`,
      500: `{${savedTheme.palette}.500}`,
      600: `{${savedTheme.palette}.600}`,
      700: `{${savedTheme.palette}.700}`,
      800: `{${savedTheme.palette}.800}`,
      900: `{${savedTheme.palette}.900}`,
      950: `{${savedTheme.palette}.950}`
    }
  },
  components: {
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            checkedBackground: '{primary.500}',
            checkedBorderColor: '{primary.500}',
            checkedColor: '#ffffff'
          },
          content: {
            checkedBackground: '{primary.500}'
          }
        },
        dark: {
          root: {
            checkedBackground: '{primary.500}',
            checkedBorderColor: '{primary.500}',
            checkedColor: '#ffffff'
          },
          content: {
            checkedBackground: '{primary.500}'
          }
        }
      }
    }
  }
});

const app = createApp(App);

app.use(PrimeVue, {
  theme: {
    preset: CustomAura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: false
    }
  }
});

app.use(router);

app.mount('#app');
